/**
 * Worker APIクライアント（§8.1）。認証は匿名デバイスUUIDのBearer。
 * 生の位置・軌跡はここを通らない（送るのは日次集計・商品メタ・購読のみ、§2）。
 */
import { isFoodTagId, isTerrainTileBuffer, parseGeoResults } from "@uvk/shared";
import type { DailyAggregate, FoodTagId, GeoResult, ScanResult, WxResponse } from "@uvk/shared";

const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? "";

export function deviceId(): string {
  let id = localStorage.getItem("uvk_device_id");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("uvk_device_id", id);
  }
  return id;
}

async function call(path: string, init: RequestInit = {}): Promise<Response> {
  return fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      authorization: `Bearer ${deviceId()}`,
      "content-type": "application/json",
      ...(init.headers ?? {}),
    },
  });
}

export async function fetchWx(cell: string): Promise<WxResponse | null> {
  try {
    const res = await fetch(`${API_BASE}/v1/wx?cell=${encodeURIComponent(cell)}`);
    if (!res.ok) return null;
    return (await res.json()) as WxResponse;
  } catch {
    return null;
  }
}

/**
 * 目的地のテキスト検索。Worker（KVキャッシュ＋規約準拠のUser-Agent）を優先し、
 * Worker未接続の開発時のみブラウザからNominatimを直接叩く。
 * 送るのは検索語だけで、現在地や識別子は渡さない（§2）。
 */
export async function geocode(q: string, viewbox?: string): Promise<GeoResult[]> {
  const params = new URLSearchParams({ q });
  if (viewbox) params.set("viewbox", viewbox);
  try {
    const res = await fetch(`${API_BASE}/v1/geocode?${params}`);
    if (res.ok) {
      const body = (await res.json()) as { results?: GeoResult[] };
      if (Array.isArray(body.results)) return body.results;
    }
  } catch {
    /* Worker未接続 → 直接フォールバックへ */
  }
  try {
    const direct = new URLSearchParams({
      q,
      format: "jsonv2",
      limit: "5",
      countrycodes: "jp",
      "accept-language": "ja",
    });
    if (viewbox) direct.set("viewbox", viewbox);
    const res = await fetch(`https://nominatim.openstreetmap.org/search?${direct}`, {
      headers: { accept: "application/json" },
    });
    if (!res.ok) return [];
    return parseGeoResults(await res.json());
  } catch {
    return [];
  }
}

export async function postAggregates(agg: DailyAggregate): Promise<boolean> {
  try {
    const res = await call("/v1/aggregates", { method: "POST", body: JSON.stringify(agg) });
    return res.ok;
  } catch {
    return false;
  }
}

/** 写真→VLM抽出（§8.4）。返り値はユーザー確認UIを必ず経由して保存する */
export async function scanProduct(imageBlob: Blob): Promise<ScanResult | null> {
  try {
    const res = await fetch(`${API_BASE}/v1/products/scan`, {
      method: "POST",
      headers: {
        authorization: `Bearer ${deviceId()}`,
        "content-type": imageBlob.type || "image/jpeg",
      },
      body: imageBlob,
    });
    if (!res.ok) return null;
    return (await res.json()) as ScanResult;
  } catch {
    return null;
  }
}

/** レシート写真→品目抽出（§8.4と同じく画像は推論後に破棄される） */
export async function scanReceipt(
  imageBlob: Blob,
): Promise<{ name: string; quantity: number; category: FoodTagId | null }[]> {
  try {
    const res = await fetch(`${API_BASE}/v1/receipts/scan`, {
      method: "POST",
      headers: {
        authorization: `Bearer ${deviceId()}`,
        "content-type": imageBlob.type || "image/jpeg",
      },
      body: imageBlob,
    });
    if (!res.ok) return [];
    const body = (await res.json()) as { items?: unknown[] };
    if (!Array.isArray(body.items)) return [];
    // VLM由来の値は無検証で採用しない（§8.4と同じ方針）
    return body.items.flatMap((raw) => {
      const o = (raw ?? {}) as Record<string, unknown>;
      const name = typeof o.name === "string" ? o.name.slice(0, 40) : "";
      const q = Number(o.quantity);
      if (!name || !Number.isFinite(q) || q <= 0) return [];
      return [{ name, quantity: q, category: isFoodTagId(o.category) ? o.category : null }];
    });
  } catch {
    return [];
  }
}

export async function saveProductMeta(p: {
  id: string;
  spf: number;
  plus: boolean;
  pa: number;
  waterproof: boolean;
  form: string | null;
}): Promise<void> {
  await call("/v1/products", { method: "POST", body: JSON.stringify(p) }).catch(() => {});
}

export async function subscribePush(sub: PushSubscription): Promise<boolean> {
  try {
    const res = await call("/v1/push/subscribe", { method: "POST", body: JSON.stringify(sub.toJSON()) });
    return res.ok;
  } catch {
    return false;
  }
}

export async function createSchedule(startTs: number, label: string): Promise<boolean> {
  try {
    const res = await call("/v1/schedules", {
      method: "POST",
      body: JSON.stringify({ id: crypto.randomUUID(), startTs, label }),
    });
    return res.ok;
  } catch {
    return false;
  }
}

/** サーバ側データ全削除（§11.3） */
export async function deleteMe(): Promise<boolean> {
  try {
    const res = await call("/v1/me", { method: "DELETE" });
    return res.ok;
  } catch {
    return false;
  }
}

/**
 * 地形タイルを取る。R2（Worker経由）→ 同梱デモタイル の順に試す。
 *
 * HTTPステータスではなく**中身のマジックナンバー**で採否を決める。
 * 静的配信は未知のパスに index.html を200で返すことがあり、
 * res.ok だけを見るとHTMLをタイルとして読んでしまう（本番ビルドで実際に起きた）。
 */
export async function fetchTerrainTile(name: string): Promise<ArrayBuffer | null> {
  const sources = [
    // APIの宛先が設定されているときだけR2を試す（未設定なら無駄な404を出さない）
    ...(API_BASE ? [`${API_BASE}/v1/terrain/${name}`] : []),
    `./demo/${name}`,
  ];
  for (const url of sources) {
    try {
      const res = await fetch(url);
      if (!res.ok) continue;
      const buf = await res.arrayBuffer();
      if (isTerrainTileBuffer(buf)) return buf;
      console.warn(`terrain: ${url} は地形タイルではありませんでした（別の応答が返っています）`);
    } catch {
      /* 次の候補へ */
    }
  }
  return null;
}
